import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  listCollectionPointsForSite,
  createCollectionPoint,
  updateCollectionPoint,
  deleteCollectionPoint,
} from '../modules/collection-points/api';

// Accept both ',' and '.' as decimal separators (Swiss operators type "0,5").
// Mirrors the pickup-weight modal parser (see CLAUDE.md "comma + zero handling").
const numParser = (v) =>
  v == null
    ? ''
    : String(v)
        .replace(/,/g, '.')
        .replace(/[^\d.]/g, '');
const numFormatter = (v) => (v == null || v === '' ? '' : String(v));

// OVO-206 — manage the collection boxes ("Caisses") at a Site, inline in the
// Site editor. A box carries an optional number (externalRef) + capacity, which
// drives the OVO-170 fill-rate suggestions. Several boxes can sit at one Site
// (e.g. EPI compost + household; CT box 7752 + 7753) — that's the point of the
// model: no need to fake N Sites at one address.
export default function CollectionPointsSection({ siteId, partnerId }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const refresh = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    const { response, error } = await listCollectionPointsForSite(siteId);
    if (error) {
      message.error(error.message || 'Failed to load collection points');
    } else if (Array.isArray(response)) {
      setPoints(response);
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isArchived: false });
    setEditorOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    form.setFieldsValue({
      name: p.name,
      externalRef: p.externalRef,
      capacityKg: p.capacityKg,
      minKg: p.minKg,
      notes: p.notes,
      isArchived: p.isArchived,
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    const payload = {
      name: values.name?.trim(),
      externalRef: values.externalRef?.trim() || null,
      capacityKg: values.capacityKg == null ? null : Number(values.capacityKg),
      minKg: values.minKg == null ? null : Number(values.minKg),
      notes: values.notes ?? null,
      isArchived: !!values.isArchived,
    };
    const { error } = editing
      ? await updateCollectionPoint(editing.id, payload)
      : await createCollectionPoint(partnerId, { siteId, ...payload });
    setSaving(false);
    if (error) {
      message.error(error.message || 'Save failed');
      return;
    }
    message.success(editing ? 'Collection point updated' : 'Collection point created');
    setEditorOpen(false);
    refresh();
  };

  const handleDelete = async (id) => {
    const { error } = await deleteCollectionPoint(id);
    if (error) {
      message.error(error.message || 'Delete failed');
      return;
    }
    message.success('Collection point deleted');
    refresh();
  };

  const columns = [
    {
      title: 'Box',
      dataIndex: 'name',
      key: 'name',
      render: (name, r) => (
        <Space size={4}>
          {r.externalRef ? (
            <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
              {r.externalRef}
            </Tag>
          ) : null}
          <span>{name}</span>
          {r.isArchived ? <Tag>archived</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Capacity (kg)',
      dataIndex: 'capacityKg',
      key: 'capacityKg',
      width: 110,
      render: (v) => (v == null ? '—' : v),
    },
    {
      title: 'Min (kg)',
      dataIndex: 'minKg',
      key: 'minKg',
      width: 90,
      render: (v) => (v == null ? '—' : v),
    },
    {
      title: 'Orders',
      key: 'orders',
      width: 70,
      render: (_, r) => r._count?.orders ?? 0,
    },
    {
      title: '',
      key: 'actions',
      width: 84,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          </Tooltip>
          <Popconfirm
            title="Delete this collection point?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      size="small"
      title="Collection points (Caisses)"
      style={{ marginTop: 16 }}
      extra={
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add box
        </Button>
      }
    >
      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>
        Boxes / containers collected at this location. Capacity drives the fill-rate suggestions; several boxes can
        share one address.
      </div>
      {points.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No collection points at this site yet" />
      ) : (
        <Table size="small" rowKey="id" loading={loading} columns={columns} dataSource={points} pagination={false} />
      )}

      <Modal
        title={editing ? `Edit collection point — ${editing.name}` : 'Add collection point'}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Compost, Bac textile Nord, or the box number" />
          </Form.Item>
          <Form.Item name="externalRef" label="Box number (optional)">
            <Input placeholder="e.g. 7752" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="capacityKg" label="Capacity (kg)" style={{ flex: 1 }}>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="e.g. 250"
                parser={numParser}
                formatter={numFormatter}
              />
            </Form.Item>
            <Form.Item name="minKg" label="Min (kg)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} parser={numParser} formatter={numFormatter} />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="isArchived" label="Archived" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
